import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { Box, IconButton, Skeleton, Typography, useTheme } from "@mui/material";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import type { ResearchFeedbackDetail } from "models/ResearchFindings/ResearchFindings";
import type ResearchFindingStore from "./ResearchFindingStore";
import { formatDateTime } from "./researchFindingsUtils";

type Props = {
    store: ResearchFindingStore;
    questionId: string;
    /** Bump to force a reload (e.g. after new feedback is submitted). */
    refreshToken: number;
};

/** Expandable full feedback history for a question, with skeletons, empty state, and delete. */
const QuestionFeedbackList: React.FC<Props> = observer(
    ({ store, questionId, refreshToken }) => {
        const theme = useTheme();
        const [items, setItems] = useState<ResearchFeedbackDetail[]>([]);
        const [loading, setLoading] = useState(true);
        const [reloadCounter, setReloadCounter] = useState(0);
        const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(
            null,
        );
        const [isDeleting, setIsDeleting] = useState(false);

        useEffect(() => {
            let cancelled = false;
            setLoading(true);
            store
                .getQuestionFeedback(questionId)
                .then((list) => {
                    if (!cancelled) {
                        setItems(list);
                        setLoading(false);
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setItems([]);
                        setLoading(false);
                    }
                });
            return () => {
                cancelled = true;
            };
        }, [store, questionId, refreshToken, reloadCounter]);

        const confirmDelete = async () => {
            if (!pendingDeleteId) return;
            setIsDeleting(true);
            const ok = await store.deleteQuestionFeedback(
                questionId,
                pendingDeleteId,
            );
            setIsDeleting(false);
            if (ok) {
                setPendingDeleteId(null);
                setReloadCounter((c) => c + 1);
            }
        };

        if (loading) {
            return (
                <Box
                    sx={{
                        mt: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                    }}
                >
                    {Array.from({ length: 3 }, (_, i) => (
                        <Box
                            key={`fb-skeleton-${i}`}
                            sx={{ display: "flex", gap: 1.5 }}
                        >
                            <Skeleton
                                variant="circular"
                                width={24}
                                height={24}
                                sx={{ backgroundColor: theme.palette.gray[300] }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Skeleton
                                    variant="text"
                                    width="35%"
                                    height={16}
                                    sx={{
                                        backgroundColor: theme.palette.gray[300],
                                    }}
                                />
                                <Skeleton
                                    variant="text"
                                    width="80%"
                                    height={16}
                                    sx={{
                                        backgroundColor: theme.palette.gray[300],
                                    }}
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>
            );
        }

        if (items.length === 0) {
            return (
                <Box
                    sx={{
                        mt: 1,
                        py: 3,
                        textAlign: "center",
                        borderRadius: "8px",
                        border: `1px dashed ${theme.palette.gray[300]}`,
                    }}
                >
                    <Typography
                        sx={{ fontSize: "13px", color: theme.palette.gray[500] }}
                    >
                        No feedback has been left for this question yet.
                    </Typography>
                </Box>
            );
        }

        return (
            <>
                <Box
                    sx={{
                        mt: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                    }}
                >
                    {items.map((f) => (
                        <Box
                            key={f.id}
                            sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}
                        >
                            {f.sentiment === "Positive" ? (
                                <ThumbUpIcon
                                    fontSize="small"
                                    sx={{
                                        color: theme.palette.success.main,
                                        mt: 0.25,
                                    }}
                                />
                            ) : (
                                <ThumbDownIcon
                                    fontSize="small"
                                    sx={{
                                        color: theme.palette.error.main,
                                        mt: 0.25,
                                    }}
                                />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: theme.palette.gray[500],
                                    }}
                                >
                                    {f.authorName || "Unknown"} ·{" "}
                                    {formatDateTime(f.createdOn)}
                                </Typography>
                                {f.text && (
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            color: theme.palette.gray[700],
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {f.text}
                                    </Typography>
                                )}
                            </Box>
                            {f.isMine && (
                                <IconButton
                                    size="small"
                                    aria-label="Delete feedback"
                                    onClick={() => setPendingDeleteId(f.id)}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                </Box>

                <AcxConfirmationDialog
                    isOpen={!!pendingDeleteId}
                    title="Delete feedback?"
                    content="This will permanently remove your feedback on this question."
                    confirmButtonText="Delete"
                    cancelButtonText="Cancel"
                    isLoading={isDeleting}
                    onClose={() => setPendingDeleteId(null)}
                    onCancel={() => setPendingDeleteId(null)}
                    onConfirm={confirmDelete}
                />
            </>
        );
    },
);

export default QuestionFeedbackList;
